import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Flex, Text, Switch, Button, useToast, VStack, HStack,
    Badge, Divider, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    IconButton, SimpleGrid, Tooltip, Alert, AlertIcon, Input, Select,
    NumberInput, NumberInputField, Checkbox, CheckboxGroup, Stack,
    FormControl, FormLabel, FormHelperText, Code, Spinner,
} from '@chakra-ui/react';
import { FaSyncAlt, FaPaperPlane, FaEye } from 'react-icons/fa';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
    getPosVenda, updatePosVendaConfig, getPosVendaPrevia,
    getPosVendaTemplates, dispararPosVenda,
} from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const DIAS_SEMANA = [
    { valor: 1, label: 'Seg' },
    { valor: 2, label: 'Ter' },
    { valor: 3, label: 'Qua' },
    { valor: 4, label: 'Qui' },
    { valor: 5, label: 'Sex' },
    { valor: 6, label: 'Sáb' },
    { valor: 7, label: 'Dom' },
];

const STATUS_BADGE = {
    enviado: { colorScheme: 'green', label: 'enviado' },
    erro: { colorScheme: 'red', label: 'erro' },
    pulado: { colorScheme: 'gray', label: 'pulado' },
};

const MOTIVO_PULO = {
    nome_invalido: 'Cadastro sem primeiro nome utilizável (vazio, razão social ou CNPJ no nome)',
    sem_item: 'Nenhum item com nome de peça na venda',
};

const statsVazias = {
    enviados: 0, responderam: 0, taxa_resposta: 0, compraram: 0, compraram_maduros: 0,
    maduros: 0, taxa_compra: 0, taxa_base: 0, taxa_parcial: false,
    receita: 0, ticket_medio: 0, erros: 0, pulados: 0, enviados_hoje: 0,
};

const moeda = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatarData = (iso) => {
    if (!iso) return '-';
    const d = new Date(String(iso).replace(' ', 'T'));
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const PosVendaTab = () => {
    const toast = useToast();
    const [config, setConfig] = useState(null);
    const [canaisDisponiveis, setCanaisDisponiveis] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [stats, setStats] = useState(statsVazias);
    const [serie, setSerie] = useState([]);
    const [disparos, setDisparos] = useState([]);
    const [periodo, setPeriodo] = useState(30);
    const [templateInfo, setTemplateInfo] = useState(null);
    const [previa, setPrevia] = useState(null);
    const [carregandoPrevia, setCarregandoPrevia] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [enviando, setEnviando] = useState(false);

    const carregar = useCallback(async (incluirConfig = false) => {
        try {
            const { data } = await getPosVenda({ dias: periodo });
            if (incluirConfig) {
                setConfig(data.config);
                setCanaisDisponiveis(data.canais_disponiveis || []);
                setVendedores(data.vendedores || []);
            }
            setStats(data.stats || statsVazias);
            setSerie(data.serie || []);
            setDisparos(data.disparos || []);
        } catch (e) {
            toast({ title: 'Erro ao carregar o pós-venda.', status: 'error', duration: 4000, isClosable: true });
        }
    }, [toast, periodo]);

    const conferirTemplate = useCallback(async () => {
        try {
            const { data } = await getPosVendaTemplates();
            setTemplateInfo(data);
        } catch (e) {
            setTemplateInfo(null);
        }
    }, []);

    // Só a primeira carga traz a config: trocar o período não pode sobrescrever
    // o que o usuário está editando nos campos.
    const jaCarregou = useRef(false);
    useEffect(() => {
        carregar(!jaCarregou.current);
        jaCarregou.current = true;
    }, [carregar]);
    useEffect(() => { conferirTemplate(); }, [conferirTemplate]);

    const salvar = async (overrides, mensagem) => {
        setSalvando(true);
        try {
            const { data } = await updatePosVendaConfig(overrides);
            setConfig(data);
            if (mensagem) {
                toast({ title: mensagem, status: 'success', duration: 3000, isClosable: true });
            }
            return true;
        } catch (e) {
            toast({
                title: e?.response?.data?.message || 'Erro ao salvar a configuração.',
                status: 'error', duration: 4000, isClosable: true,
            });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const campo = (chave, valor) => setConfig((c) => ({ ...c, [chave]: valor }));

    const salvarTudo = () => salvar({
        tag_template: config.tag_template || null,
        tag_template_fallback: config.tag_template_fallback || null,
        dias_apos_entrega: Number(config.dias_apos_entrega),
        janela_maxima_dias: Number(config.janela_maxima_dias),
        canais: config.canais || [],
        excluir_carteira_user_ids: (config.excluir_carteira_user_ids || []).map(Number),
        excluir_com_reclamacao: !!config.excluir_com_reclamacao,
        hora_inicio: Number(config.hora_inicio),
        hora_fim: Number(config.hora_fim),
        dias_semana: (config.dias_semana || []).map(Number),
        limite_diario: Number(config.limite_diario),
        cooldown_dias: Number(config.cooldown_dias),
        valor_minimo: Number(config.valor_minimo),
        atribuicao_dias: Number(config.atribuicao_dias),
        pular_nome_invalido: !!config.pular_nome_invalido,
    }, 'Configuração salva.').then((ok) => { if (ok) { conferirTemplate(); carregarPrevia(); } });

    const carregarPrevia = async () => {
        setCarregandoPrevia(true);
        try {
            const { data } = await getPosVendaPrevia();
            setPrevia(data);
        } catch (e) {
            toast({ title: 'Erro ao carregar a prévia.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setCarregandoPrevia(false);
        }
    };

    const enviarAgora = async () => {
        setEnviando(true);
        try {
            const { data } = await dispararPosVenda({});
            toast({
                title: data.motivo
                    ? data.motivo
                    : `Enviados: ${data.enviados} · pulados: ${data.pulados} · erros: ${data.erros}`,
                status: data.motivo ? 'warning' : 'success',
                duration: 5000, isClosable: true,
            });
            carregar(false);
        } catch (e) {
            toast({ title: 'Erro ao disparar.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setEnviando(false);
        }
    };

    if (!config) {
        return <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>;
    }

    const grafico = {
        labels: serie.map((s) => {
            const d = new Date(`${s.dia}T00:00:00`);
            return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        }),
        datasets: [
            // minBarLength deixa visível o dia com 1 ou 2 respostas/compras, que
            // ao lado de uma barra de 50 envios vira um risco no eixo.
            { label: 'Enviados', data: serie.map((s) => s.enviados), backgroundColor: '#F6AD55', minBarLength: 3 },
            { label: 'Responderam', data: serie.map((s) => s.responderam), backgroundColor: '#4299E1', minBarLength: 3 },
            { label: 'Compraram depois', data: serie.map((s) => s.compraram), backgroundColor: '#48BB78', minBarLength: 3 },
        ],
    };

    return (
        <VStack spacing={5} align="stretch">

            {/* Status + liga/desliga */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                    <Box>
                        <Text fontWeight="bold" fontSize="lg">Follow-up pós-venda no WhatsApp</Text>
                        <Text fontSize="sm" color="gray.500">
                            Alguns dias depois do pedido entregue, manda o template oficial perguntando se
                            deu tudo certo com a peça. Quem responde abre conversa e cai no atendimento
                            normal. O disparo respeita a janela de horário, os dias da semana e o teto diário.
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Badge colorScheme={config.ativo ? 'green' : 'gray'} fontSize="0.8em" px={2} py={1} borderRadius="md">
                            {config.ativo ? 'LIGADO' : 'DESLIGADO'}
                        </Badge>
                        <Switch
                            size="lg" colorScheme="orange"
                            isChecked={!!config.ativo}
                            isDisabled={salvando}
                            onChange={(e) => salvar(
                                { ativo: e.target.checked },
                                e.target.checked ? 'Follow-up pós-venda ativado.' : 'Follow-up pós-venda desativado.'
                            )}
                        />
                    </HStack>
                </Flex>

                {templateInfo && templateInfo.encontrado === false && templateInfo.fallback_encontrado === true && (
                    <Alert status="info" mt={4} borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        <Box>
                            A Poli não enxerga o template principal <Code>{templateInfo.tag}</Code>, então o envio
                            vai sair pelo reserva <Code>{templateInfo.fallback}</Code>, que está cadastrado lá.
                            O reserva não leva o nome da peça — assim que a Poli cadastrar o principal, ele volta
                            a ser usado sozinho, sem mexer em nada aqui.
                        </Box>
                    </Alert>
                )}
                {templateInfo && templateInfo.encontrado === false && templateInfo.fallback_encontrado !== true && (
                    <Alert status="warning" mt={4} borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        <Box>
                            A Poli não enxerga o template <Code>{templateInfo.tag}</Code> nem um reserva válido
                            (ela lista {templateInfo.templates?.length || 0} tags). Enquanto isso, todo envio vai
                            falhar. Template criado direto no Gerenciador da Meta não fica disponível aqui — ele
                            precisa ser cadastrado no painel da Poli.
                        </Box>
                    </Alert>
                )}
                {templateInfo && templateInfo.encontrado === true && (
                    <Alert status="success" mt={4} borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        Template <Code mx={1}>{templateInfo.tag}</Code> encontrado na Poli.
                        {templateInfo.fallback_encontrado === false && ' O reserva configurado, porém, não existe lá.'}
                    </Alert>
                )}

                <Divider my={4} />

                <Flex gap={3} wrap="wrap" align="center">
                    <Button
                        size="sm" leftIcon={<FaEye />} variant="outline"
                        onClick={carregarPrevia} isLoading={carregandoPrevia}
                        w={{ base: 'full', sm: 'auto' }}
                    >
                        Ver quem entraria agora
                    </Button>
                    <Button
                        size="sm" leftIcon={<FaPaperPlane />} colorScheme="orange"
                        onClick={enviarAgora} isLoading={enviando}
                        isDisabled={!config.ativo}
                        w={{ base: 'full', sm: 'auto' }}
                    >
                        Enviar agora
                    </Button>
                    <Text fontSize="xs" color="gray.500">
                        Enviados hoje: <b>{stats.enviados_hoje}</b> de {config.limite_diario}
                    </Text>
                </Flex>

                {previa && (
                    <Box mt={4} borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                            {previa.total} pedido(s) entrariam agora
                            {previa.total >= previa.limitado_em && ' (contagem limitada a 200)'}
                        </Text>
                        {previa.amostra?.length > 0 && (
                            <TableContainer>
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr><Th>Venda</Th><Th>Canal</Th><Th>Mensagem que sairia</Th></Tr>
                                    </Thead>
                                    <Tbody>
                                        {previa.amostra.map((a) => (
                                            <Tr key={a.venda_id}>
                                                <Td fontSize="xs">#{a.venda_id}</Td>
                                                <Td fontSize="xs">{a.canal}</Td>
                                                <Td fontSize="xs" whiteSpace="normal">
                                                    {a.nome ? (
                                                        <>Olá, <b>{a.nome}</b>! … da peça <b>{a.peca}</b></>
                                                    ) : (
                                                        <Text color="gray.500">
                                                            pulado — cadastro "{a.nome_cadastro}" não vira primeiro nome
                                                        </Text>
                                                    )}
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}
            </Box>

            {/* Funil */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={3}>
                    <Text fontWeight="bold" fontSize="lg">Resultado</Text>
                    <HStack w={{ base: 'full', sm: 'auto' }}>
                        <Select
                            size="sm" w={{ base: 'full', sm: '150px' }} value={periodo}
                            onChange={(e) => setPeriodo(Number(e.target.value))}
                        >
                            <option value={7}>Últimos 7 dias</option>
                            <option value={30}>Últimos 30 dias</option>
                            <option value={90}>Últimos 90 dias</option>
                            <option value={180}>Últimos 180 dias</option>
                        </Select>
                        <IconButton aria-label="Atualizar" icon={<FaSyncAlt />} size="sm" variant="ghost" onClick={() => carregar(false)} />
                    </HStack>
                </Flex>

                <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Mensagens enviadas</Text>
                        <Text fontSize="xl" fontWeight="bold">{stats.enviados}</Text>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Responderam</Text>
                        <Text fontSize="xl" fontWeight="bold" color="blue.500">{stats.responderam}</Text>
                        <Text fontSize="xs" color="gray.400">{stats.taxa_resposta}% de resposta</Text>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Tooltip
                            label={stats.taxa_parcial
                                ? `Clientes que voltaram a comprar em até ${config.atribuicao_dias} dias depois da mensagem. A taxa ainda é parcial: nenhum disparo do período completou a janela de atribuição, então ela sai sobre os ${stats.taxa_base} enviados.`
                                : `Clientes que voltaram a comprar em até ${config.atribuicao_dias} dias depois da mensagem. A taxa sai só dos ${stats.maduros} disparos com a janela já vencida (${stats.compraram_maduros} compraram).`}
                        >
                            <Box>
                                <Text fontSize="xs" color="gray.500">Compraram depois</Text>
                                <Text fontSize="xl" fontWeight="bold" color="green.600">{stats.compraram}</Text>
                                <Text fontSize="xs" color="gray.400">
                                    {stats.taxa_compra}% de {stats.taxa_base}{stats.taxa_parcial ? ' (parcial)' : ' maduros'}
                                </Text>
                            </Box>
                        </Tooltip>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Receita atribuída</Text>
                        <Text fontSize="xl" fontWeight="bold" color="green.600">{moeda(stats.receita)}</Text>
                        <Text fontSize="xs" color="gray.400">ticket {moeda(stats.ticket_medio)}</Text>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Pulados</Text>
                        <Text fontSize="xl" fontWeight="bold" color="gray.500">{stats.pulados}</Text>
                        <Text fontSize="xs" color="gray.400">cadastro sem nome válido</Text>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Erros de envio</Text>
                        <Text fontSize="xl" fontWeight="bold" color="red.500">{stats.erros}</Text>
                        {stats.via_fallback > 0 && (
                            <Text fontSize="xs" color="purple.500">{stats.via_fallback} pelo reserva</Text>
                        )}
                    </Box>
                </SimpleGrid>

                {serie.length > 0 && (
                    <Box mt={5}>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                            Cada barra é o que aconteceu no dia: envio na data do envio, resposta na data
                            da resposta e compra na data da compra.
                        </Text>
                        <Box h="260px">
                            <Bar
                                data={grafico}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: { x: { stacked: false }, y: { beginAtZero: true, ticks: { precision: 0 } } },
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Configuração */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Configuração do disparo</Text>
                <Text fontSize="sm" color="gray.600" mb={4}>
                    O robô roda de hora em hora e só envia dentro da janela abaixo.
                </Text>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                    <FormControl>
                        <FormLabel fontSize="sm">Tag do template na Poli</FormLabel>
                        <Input
                            size="sm" value={config.tag_template || ''}
                            onChange={(e) => campo('tag_template', e.target.value)}
                        />
                        <FormHelperText fontSize="xs">Precisa existir na Poli, não só na Meta.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Template reserva</FormLabel>
                        <Input
                            size="sm" value={config.tag_template_fallback || ''}
                            onChange={(e) => campo('tag_template_fallback', e.target.value)}
                        />
                        <FormHelperText fontSize="xs">
                            Usado só quando o principal falha. As variáveis se ajustam sozinhas ao
                            que cada template aceita.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Dias depois da entrega</FormLabel>
                        <NumberInput size="sm" min={0} max={180} value={config.dias_apos_entrega}
                            onChange={(v) => campo('dias_apos_entrega', v)}>
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">Conta a partir da conclusão do pedido.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Não enviar se entregue há mais de</FormLabel>
                        <NumberInput size="sm" min={1} max={365} value={config.janela_maxima_dias}
                            onChange={(v) => campo('janela_maxima_dias', v)}>
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">Dias. Evita mandar sobre pedido velho.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Teto de envios por dia</FormLabel>
                        <NumberInput size="sm" min={0} max={2000} value={config.limite_diario}
                            onChange={(v) => campo('limite_diario', v)}>
                            <NumberInputField />
                        </NumberInput>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Não repetir pro mesmo cliente por</FormLabel>
                        <NumberInput size="sm" min={0} max={365} value={config.cooldown_dias}
                            onChange={(v) => campo('cooldown_dias', v)}>
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">Dias.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Valor mínimo do pedido</FormLabel>
                        <NumberInput size="sm" min={0} precision={2} value={config.valor_minimo}
                            onChange={(v) => campo('valor_minimo', v)}>
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">R$ 0 envia para todos.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Enviar entre</FormLabel>
                        <HStack>
                            <NumberInput size="sm" min={0} max={23} value={config.hora_inicio}
                                onChange={(v) => campo('hora_inicio', v)}>
                                <NumberInputField />
                            </NumberInput>
                            <Text fontSize="sm">e</Text>
                            <NumberInput size="sm" min={1} max={24} value={config.hora_fim}
                                onChange={(v) => campo('hora_fim', v)}>
                                <NumberInputField />
                            </NumberInput>
                        </HStack>
                        <FormHelperText fontSize="xs">Horas do dia (0 a 24).</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Janela de atribuição de compra</FormLabel>
                        <NumberInput size="sm" min={1} max={180} value={config.atribuicao_dias}
                            onChange={(v) => campo('atribuicao_dias', v)}>
                            <NumberInputField />
                        </NumberInput>
                        <FormHelperText fontSize="xs">
                            Dias após a mensagem em que uma compra nova conta como resultado.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Cadastro sem primeiro nome</FormLabel>
                        <Checkbox
                            colorScheme="orange" isChecked={!!config.pular_nome_invalido}
                            onChange={(e) => campo('pular_nome_invalido', e.target.checked)}
                        >
                            <Text fontSize="sm">Pular o cliente</Text>
                        </Checkbox>
                        <FormHelperText fontSize="xs">
                            Desmarcado, envia "Olá, tudo bem!" em vez do nome.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Pedido com reclamação aberta</FormLabel>
                        <Checkbox
                            colorScheme="orange" isChecked={!!config.excluir_com_reclamacao}
                            onChange={(e) => campo('excluir_com_reclamacao', e.target.checked)}
                        >
                            <Text fontSize="sm">Não enviar</Text>
                        </Checkbox>
                        <FormHelperText fontSize="xs">
                            Evita perguntar "deu tudo certo?" para quem está reclamando da peça.
                            Fechada a reclamação, o pedido volta para a fila.
                        </FormHelperText>
                    </FormControl>
                </SimpleGrid>

                <Divider my={5} />

                <FormControl mb={4}>
                    <FormLabel fontSize="sm">Canais</FormLabel>
                    <CheckboxGroup
                        colorScheme="orange"
                        value={config.canais || []}
                        onChange={(v) => campo('canais', v)}
                    >
                        <Stack direction="row" wrap="wrap" spacing={4}>
                            {canaisDisponiveis.map((c) => (
                                <Checkbox key={c} value={c}><Text fontSize="sm">{c}</Text></Checkbox>
                            ))}
                        </Stack>
                    </CheckboxGroup>
                    <FormHelperText fontSize="xs">
                        Só entram pedidos cujo cliente tem telefone utilizável no cadastro — no Mercado
                        Livre a maioria vem sem telefone.
                    </FormHelperText>
                </FormControl>

                <FormControl mb={4}>
                    <FormLabel fontSize="sm">Não enviar para carteira de</FormLabel>
                    {vendedores.length === 0 ? (
                        <Text fontSize="sm" color="gray.400">Nenhum vendedor com acesso ao CRM.</Text>
                    ) : (
                        <CheckboxGroup
                            colorScheme="orange"
                            value={(config.excluir_carteira_user_ids || []).map(String)}
                            onChange={(v) => campo('excluir_carteira_user_ids', v.map(Number))}
                        >
                            <Stack direction="row" wrap="wrap" spacing={4}>
                                {vendedores.map((v) => (
                                    <Checkbox key={v.id} value={String(v.id)}>
                                        <Text fontSize="sm">{v.name}</Text>
                                    </Checkbox>
                                ))}
                            </Stack>
                        </CheckboxGroup>
                    )}
                    <FormHelperText fontSize="xs">
                        Cliente que está na carteira de um vendedor marcado fica de fora do disparo —
                        quem fala com ele é o vendedor. Desmarcar libera o cliente de novo.
                    </FormHelperText>
                </FormControl>

                <FormControl mb={5}>
                    <FormLabel fontSize="sm">Dias da semana</FormLabel>
                    <CheckboxGroup
                        colorScheme="orange"
                        value={(config.dias_semana || []).map(String)}
                        onChange={(v) => campo('dias_semana', v.map(Number))}
                    >
                        <Stack direction="row" wrap="wrap" spacing={4}>
                            {DIAS_SEMANA.map((d) => (
                                <Checkbox key={d.valor} value={String(d.valor)}>
                                    <Text fontSize="sm">{d.label}</Text>
                                </Checkbox>
                            ))}
                        </Stack>
                    </CheckboxGroup>
                </FormControl>

                <Button colorScheme="orange" size="sm" onClick={salvarTudo} isLoading={salvando}>
                    Salvar configuração
                </Button>
            </Box>

            {/* Disparos */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" mb={3}>
                    <Box>
                        <Text fontWeight="bold" fontSize="lg">Últimos disparos</Text>
                        <Text fontSize="sm" color="gray.500">
                            O que saiu, quem respondeu e quem voltou a comprar.
                        </Text>
                    </Box>
                    <IconButton aria-label="Atualizar" icon={<FaSyncAlt />} size="sm" variant="ghost" onClick={() => carregar(false)} />
                </Flex>

                {disparos.length === 0 ? (
                    <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>
                        Nenhum disparo ainda.
                    </Text>
                ) : (
                    <TableContainer>
                        <Table size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Quando</Th>
                                    <Th>Cliente</Th>
                                    <Th>Peça</Th>
                                    <Th>Canal</Th>
                                    <Th>Status</Th>
                                    <Th>Respondeu</Th>
                                    <Th>Comprou depois</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {disparos.map((d) => {
                                    const badge = STATUS_BADGE[d.status] || { colorScheme: 'gray', label: d.status };
                                    return (
                                        <Tr key={d.id}>
                                            <Td fontSize="xs" whiteSpace="nowrap">{formatarData(d.enviado_em || d.created_at)}</Td>
                                            <Td fontSize="xs" maxW={{ base: '130px', md: '180px' }} whiteSpace="normal">
                                                <Text noOfLines={1}>{d.cliente_nome || '-'}</Text>
                                                <Text color="gray.400">#{d.venda_id} · {moeda(d.valor_pedido)}</Text>
                                            </Td>
                                            <Td fontSize="xs" maxW={{ base: '150px', md: '220px' }} whiteSpace="normal">
                                                {d.param_peca || '-'}
                                                {d.usou_fallback ? (
                                                    <Tooltip label={`Saiu pelo template reserva "${d.tag_usada}" porque o principal falhou`}>
                                                        <Badge colorScheme="purple" ml={1}>reserva</Badge>
                                                    </Tooltip>
                                                ) : null}
                                            </Td>
                                            <Td fontSize="xs">{d.canal || '-'}</Td>
                                            <Td>
                                                <Tooltip label={d.erro || MOTIVO_PULO[d.motivo_pulo] || ''}>
                                                    <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
                                                </Tooltip>
                                            </Td>
                                            <Td fontSize="xs" maxW={{ base: '150px', md: '200px' }} whiteSpace="normal">
                                                {d.respondido_em ? (
                                                    <>
                                                        <Badge colorScheme="blue" mb={1}>sim</Badge>
                                                        <Text color="gray.500" noOfLines={2}>{d.resposta_texto}</Text>
                                                    </>
                                                ) : (
                                                    <Text color="gray.400">-</Text>
                                                )}
                                            </Td>
                                            <Td fontSize="xs" whiteSpace="nowrap">
                                                {d.compra_venda_id ? (
                                                    <>
                                                        <Badge colorScheme="green">#{d.compra_venda_id}</Badge>
                                                        <Text color="gray.500">{moeda(d.valor_comprado)}</Text>
                                                    </>
                                                ) : (
                                                    <Text color="gray.400">-</Text>
                                                )}
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

        </VStack>
    );
};

export default PosVendaTab;
